import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Users, Bell, Settings, LogOut, Menu, X, MessageSquare, Mail,
  LayoutDashboard, UserPlus, Shield, ArrowRightLeft
} from 'lucide-react';
import { Button } from './ui/button';
import SessionsDialog from './auth/SessionsDialog';
import VersionFooter from './VersionFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [feedbackUnread, setFeedbackUnread] = useState(0);
  const [messageUnread, setMessageUnread] = useState(0);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/accountant/feedback/unread-count`);
        setFeedbackUnread(res.data.unread_count || 0);
      } catch (err) {
        // ignore
      }
    };
    if (user) {
      fetchUnread();
      const iv = setInterval(fetchUnread, 30000);
      return () => clearInterval(iv);
    }
  }, [user]);

  // Fetch unread message count
  useEffect(() => {
    const fetchMsgUnread = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/messages/unread-count`);
        setMessageUnread(res.data.unread_count || 0);
      } catch {
        // ignore
      }
    };
    if (user) {
      fetchMsgUnread();
      const iv = setInterval(fetchMsgUnread, 15000);
      return () => clearInterval(iv);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/accountant/login');
  };

  const isSuperAdmin = user?.role === 'superadmin';

  const navItems = [
    { path: '/accountant/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/accountant/link-client', icon: UserPlus, label: 'Link Client' },
    { path: '/accountant/messages', icon: Mail, label: 'Messages', badge: messageUnread },
    { path: '/accountant/notifications', icon: Bell, label: 'Notifications' },
    { path: '/accountant/team', icon: Users, label: 'Team' },
    { path: '/accountant/feedback', icon: MessageSquare, label: 'Feedback', badge: feedbackUnread },
    { path: '/accountant/settings', icon: Settings, label: 'Settings' },
  ];

  // Admin page only for superadmin
  if (isSuperAdmin) {
    navItems.push({ path: '/accountant/admin', icon: Shield, label: 'Admin' });
  }

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path ||
      (item.path === '/accountant/dashboard' && location.pathname.startsWith('/accountant/client/'));
    return (
      <Link
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
          isActive
            ? 'bg-sky-600 text-white shadow-lg shadow-sky-600/30'
            : 'text-slate-400 hover:text-white hover:bg-slate-800'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <item.icon className="h-4 w-4" />
        <span className="font-medium flex-1">{item.label}</span>
        {item.badge > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="feedback-unread-badge">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-800 border-b border-slate-700 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-sky-400" />
          <span className="font-bold text-white text-sm">Accountant Portal</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-slate-800 border-r border-slate-700 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          <div className="h-14 flex items-center justify-between px-5 border-b border-slate-700 shrink-0">
            <div className="flex items-center gap-3">
              <Shield className="h-7 w-7 text-sky-400" />
              <div>
                <h1 className="font-bold text-white text-sm">Accountant Portal</h1>
                <p className="text-[10px] text-slate-500">Seafarer Logbook</p>
              </div>
            </div>
          </div>

          {/* Switch to User Site — Admin+ only */}
          {isSuperAdmin && (
            <div className="px-3 pt-2">
              <button onClick={() => navigate('/trips')}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                data-testid="toggle-admin-panel">
                <ArrowRightLeft className="h-4 w-4 text-amber-400" />
                <span className="text-xs font-medium text-amber-400">Seafarer Site</span>
              </button>
            </div>
          )}

          <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto min-h-0">
            {navItems.map(item => (
              <NavLink key={item.path} item={item} />
            ))}
          </nav>

          <div className="px-3 py-2.5 border-t border-slate-700 shrink-0">
            <div className="flex items-center gap-2.5 mb-1.5 px-1">
              <div className="w-8 h-8 rounded-full bg-sky-600 flex items-center justify-center shrink-0">
                <span className="text-white font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate leading-tight">{user?.name}</p>
                <p className="text-[11px] text-slate-400 truncate leading-tight">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => {
                setSessionsOpen(true);
                setSidebarOpen(false);
              }}
              data-testid="accountant-sessions-btn"
              className="w-full justify-start text-slate-400 hover:text-amber-400 hover:bg-amber-400/10 h-8 text-sm"
            >
              <Shield className="h-4 w-4 mr-2" />
              Active Sessions
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="accountant-sign-out-btn"
              className="w-full justify-start text-slate-400 hover:text-red-400 hover:bg-red-400/10 h-8 text-sm"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
            <p className="text-[9px] text-slate-600 text-center mt-2 select-none" data-testid="copyright-notice">
              &copy; 2026 D Grassie. All rights reserved.
            </p>
            <VersionFooter />
          </div>
        </div>
      </aside>

      <SessionsDialog open={sessionsOpen} onOpenChange={setSessionsOpen} />

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen overflow-x-hidden">
        <div className="p-4 sm:p-6 space-y-4">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AccountantLayout;
